<template>
    <div class="с">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import { Component, Ref, Vue } from 'vue-property-decorator';

@Component({})
class VChristmasTreeLoader extends Vue {
    @Ref('canvas') canvasRef;

    ctx = null;
    animationFrameId = 0;
    rotationZ = 0;

    chains = [
        {
            bulbRadius: 7,
            bulbsCount: 100,
            endColor: '#2da418',
            glowOffset: 0,
            opacity: 1,
            startAngle: 0,
            startColor: '#18a452',
            turnsCount: 14,
        },
        {
            bulbRadius: 50,
            bulbsCount: 20,
            endColor: '#13d116',
            glowOffset: 0,
            opacity: 0.5,
            startAngle: 120,
            startColor: '#18a452',
            turnsCount: 3,
        },
        {
            bulbRadius: 12,
            bulbsCount: 50,
            endColor: '#13d116',
            glowOffset: 0,
            opacity: 0.8,
            startAngle: 240,
            startColor: '#18a452',
            turnsCount: -3,
        },
        {
            bulbRadius: 7,
            bulbsCount: 196,
            endColor: '#e9841d',
            glowOffset: 0,
            opacity: 0.8,
            startAngle: 240,
            startColor: '#e9a01b',
            turnsCount: -3,
        },
    ];

    width = 0;
    height = 0;
    treeHeight = 0;
    baseRadius = 0;
    baseCenter = { x: 0, y: 0 };
    tiltAngle = 0;
    rotationX = 30;

    mounted() {
        this.ctx = this.canvasRef.getContext('2d');
        this.resize();
        window.addEventListener('resize', this.resize);
        this.loop();
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.resize);
        cancelAnimationFrame(this.animationFrameId);
    }

    resize() {
        if (!this.canvasRef || !this.ctx) return;

        const parent = this.canvasRef.parentElement;
        const clientWidth = parent ? parent.clientWidth : window.innerWidth;
        const clientHeight = parent ? parent.clientHeight : window.innerHeight;

        const pixelRatio = window.devicePixelRatio || 1;

        this.width = clientWidth;
        this.height = clientHeight;

        this.canvasRef.width = this.width * pixelRatio;
        this.canvasRef.height = this.height * pixelRatio;

        this.ctx.scale(pixelRatio, pixelRatio);

        this.tiltAngle = (this.rotationX / 180) * Math.PI;
        this.treeHeight = Math.min(this.width, this.height) * 0.8;
        this.baseRadius = this.treeHeight * 0.3;

        this.baseCenter = {
            x: this.width / 2,
            y:
                this.height / 2 +
                (this.treeHeight / 2) * Math.cos(this.tiltAngle) -
                (this.baseRadius / 2) * Math.sin(this.tiltAngle),
        };
    }

    loop() {
        if (this.ctx) {
            this.ctx.clearRect(0, 0, this.width, this.height);

            this.rotationZ = (this.rotationZ - 1) % 360;

            this.chains.forEach((chain) => this.renderChain(chain));
        }
        this.animationFrameId = requestAnimationFrame(this.loop);
    }

    renderChain(props) {
        if (!this.ctx) return;

        for (let i = 0; i < props.bulbsCount; i++) {
            let progress = i / (props.bulbsCount - 1);
            progress = Math.pow(progress, Math.sqrt(progress) + 1);

            const turnProgress = (progress * props.turnsCount) % 1;
            const sectionRadius = this.baseRadius * (1 - progress);

            const sectionAngle =
                (((turnProgress * 360 + props.startAngle + this.rotationZ) / 180) * Math.PI) % (Math.PI * 2);

            const X = this.baseCenter.x + Math.sin(sectionAngle) * sectionRadius;
            const Y =
                this.baseCenter.y -
                progress * this.treeHeight * Math.sin(((90 - this.rotationX) / 180) * Math.PI) +
                sectionRadius * Math.sin(this.tiltAngle) * Math.cos(sectionAngle);

            const depthOpacity = Math.min(1, Math.max(0.35, Math.cos(sectionAngle)) + 0.3);

            const bulbRadius = (props.bulbRadius * this.treeHeight) / 1000;
            const glowRadius = ((props.bulbRadius + props.glowOffset) * this.treeHeight) / 1000;

            const mixedColor = this.mixColor(props.startColor, props.endColor, progress);
            const currentColor = this.applyOpacity(mixedColor, depthOpacity);

            this.ctx.globalAlpha = props.opacity;

            if (props.glowOffset > 0) {
                const gradient = this.ctx.createRadialGradient(X, Y, bulbRadius, X, Y, glowRadius);
                gradient.addColorStop(0, this.applyOpacity(this.mixColor(mixedColor, '#fff', 0.1), 0.5));
                gradient.addColorStop(0.5, this.applyOpacity(mixedColor, 0.3));
                gradient.addColorStop(1, this.applyOpacity(mixedColor, 0));

                this.ctx.fillStyle = gradient;
                this.ctx.beginPath();
                this.ctx.arc(X, Y, glowRadius, 0, 2 * Math.PI);
                this.ctx.fill();
            }

            this.ctx.fillStyle = currentColor;
            this.ctx.beginPath();
            this.ctx.arc(X, Y, bulbRadius, 0, 2 * Math.PI);
            this.ctx.fill();
        }
    }

    hexToRgb(hex) {
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [255, 255, 255];
    }

    mixColor(color1, color2, weight) {
        const rgb1 = this.hexToRgb(color1);
        const rgb2 = this.hexToRgb(color2);
        const w = weight;
        const w1 = 1 - w;
        const r = Math.round(rgb1[0] * w1 + rgb2[0] * w);
        const g = Math.round(rgb1[1] * w1 + rgb2[1] * w);
        const b = Math.round(rgb1[2] * w1 + rgb2[2] * w);
        return `rgb(${r}, ${g}, ${b})`;
    }

    applyOpacity(color, alpha) {
        if (color.startsWith('#')) {
            const [r, g, b] = this.hexToRgb(color);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        } else if (color.startsWith('rgb')) {
            return color.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
        }
        return color;
    }
}
export default VChristmasTreeLoader;
</script>

<style lang="scss">
.tree-wrapper {
    width: 100%;
    height: 100%;
    max-height: 200px;
    overflow: hidden;
    position: relative;

    background: transparent;

    canvas {
        display: block;
        width: 100%;
        height: 100%;
        outline: none;
    }
}
</style>
